/**
 * Client-side parsing of vector survey files (KML, KMZ, zipped Shapefile, GeoJSON)
 * so they can be previewed on the map before the backend upload.
 * DXF / JXL / RW5 / RAW / HTML stay backend-only.
 */

export const CLIENT_PREVIEW_TYPES = ['kml', 'kmz', 'shp', 'geojson']

const MAX_PREVIEW_POINTS = 3000

export function isClientPreviewType(type) {
  return CLIENT_PREVIEW_TYPES.includes(String(type || '').toLowerCase())
}

function readAsText(file) {
  if (typeof file?.text === 'function') return file.text()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsText(file)
  })
}

function readAsArrayBuffer(file) {
  if (typeof file?.arrayBuffer === 'function') return file.arrayBuffer()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(file)
  })
}

async function kmlTextToGeoJson(text) {
  const mod = await import(/* webpackChunkName: "geo-preview" */ '@tmcw/togeojson')
  const kml = mod.kml || mod.default?.kml
  const dom = new DOMParser().parseFromString(text, 'text/xml')
  if (dom.getElementsByTagName('parsererror').length) {
    throw new Error('ملف KML غير صالح')
  }
  return kml(dom)
}

async function kmzToGeoJson(file) {
  const { default: JSZip } = await import(/* webpackChunkName: "geo-preview" */ 'jszip')
  const zip = await JSZip.loadAsync(await readAsArrayBuffer(file))
  const entry = Object.values(zip.files).find(item => !item.dir && /\.kml$/i.test(item.name))
  if (!entry) throw new Error('ملف KMZ لا يحتوي على KML')
  return kmlTextToGeoJson(await entry.async('string'))
}

async function shapefileZipToGeoJson(file) {
  const { default: shp } = await import(/* webpackChunkName: "geo-preview" */ 'shpjs')
  const result = await shp(await readAsArrayBuffer(file))
  const collections = Array.isArray(result) ? result : [result]
  return {
    type: 'FeatureCollection',
    features: collections.flatMap(collection => collection?.features || []),
  }
}

/** @returns {Promise<{type: 'FeatureCollection', features: Array}>} */
export async function parseGeoFile(type, file) {
  const normalized = String(type || '').toLowerCase()
  if (normalized === 'kml') return kmlTextToGeoJson(await readAsText(file))
  if (normalized === 'kmz') return kmzToGeoJson(file)
  if (normalized === 'shp') return shapefileZipToGeoJson(file)
  if (normalized === 'geojson') {
    const json = JSON.parse(await readAsText(file))
    if (json?.type === 'FeatureCollection') return json
    if (json?.type === 'Feature') return { type: 'FeatureCollection', features: [json] }
    return { type: 'FeatureCollection', features: json?.type ? [{ type: 'Feature', geometry: json, properties: {} }] : [] }
  }
  throw new Error('صيغة غير مدعومة للمعاينة')
}

function collectPositions(geometry, out) {
  if (!geometry) return
  if (geometry.type === 'GeometryCollection') {
    (geometry.geometries || []).forEach(item => collectPositions(item, out))
    return
  }
  const walk = coords => {
    if (!Array.isArray(coords)) return
    if (typeof coords[0] === 'number') {
      out.push(coords)
      return
    }
    coords.forEach(walk)
  }
  walk(geometry.coordinates)
}

function featureLabel(feature) {
  const props = feature?.properties || {}
  const value = props.name ?? props.Name ?? props.NAME ?? props.label ?? props.id
  return value != null && String(value).trim() !== '' ? String(value).trim() : ''
}

/**
 * Flatten every vertex into `{ latitude, longitude, point_name, point_number }`
 * (the shape `normalizeGeodesiaPreviewPoints` expects). Closing ring vertices are dropped.
 */
export function geoJsonToPreviewPoints(collection, limit = MAX_PREVIEW_POINTS) {
  const points = []
  const seen = new Set()
  const features = Array.isArray(collection?.features) ? collection.features : []
  features.some(feature => {
    const positions = []
    collectPositions(feature?.geometry, positions)
    const label = featureLabel(feature)
    const isPoint = feature?.geometry?.type === 'Point'
    positions.forEach(([lng, lat]) => {
      if (points.length >= limit) return
      const key = `${Number(lng).toFixed(8)},${Number(lat).toFixed(8)}`
      if (seen.has(key)) return
      seen.add(key)
      const number = points.length + 1
      points.push({
        longitude: Number(lng),
        latitude: Number(lat),
        point_name: isPoint && label ? label : String(number),
        point_number: number,
      })
    })
    return points.length >= limit
  })
  return points
}

export async function previewPointsFromFile(type, file) {
  return geoJsonToPreviewPoints(await parseGeoFile(type, file))
}
